import React, { useState, useEffect } from 'react';
import { RefreshCw, Check, X } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const WithdrawalsPage = () => {
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [me, setMe] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');

    const loadMe = async () => {
        try {
            const r = await fetch(`${API_BASE_URL}/api/auth/me`, { credentials: 'include' });
            if (r.ok) setMe(await r.json());
        } catch (_) {}
    };

    const loadWithdrawals = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams();
            if (statusFilter) params.set('status_filter', statusFilter);
            const r = await fetch(`${API_BASE_URL}/api/referrals/withdrawals/list?${params.toString()}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) throw new Error('Не удалось загрузить заявки');
            const data = await r.json();
            setWithdrawals(data.withdrawals || []);
        } catch (e) {
            setError(e.message || 'Ошибка');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadMe(); }, []);
    useEffect(() => { loadWithdrawals(); }, [statusFilter]);

    const handleApprove = async (requestId) => {
        if (!me?.id) { alert('Сессия не определена'); return; }
        const comment = window.prompt('Комментарий для пользователя (необязательно):') ?? '';
        setProcessingId(requestId);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/withdrawals/${requestId}/approve`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ processed_by_user_id: me.id, admin_comment: comment || undefined }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || 'Ошибка');
            await loadWithdrawals();
        } catch (e) {
            alert(e.message || 'Ошибка');
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (requestId) => {
        if (!me?.id) { alert('Сессия не определена'); return; }
        const comment = window.prompt('Причина отклонения / комментарий для пользователя (необязательно):') ?? '';
        setProcessingId(requestId);
        try {
            const r = await fetch(`${API_BASE_URL}/api/referrals/withdrawals/${requestId}/reject`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ processed_by_user_id: me.id, admin_comment: comment || undefined }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || 'Ошибка');
            await loadWithdrawals();
        } catch (e) {
            alert(e.message || 'Ошибка');
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-2xl font-bold text-slate-800">Заявки на вывод</h2>
                <div className="flex items-center gap-2">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                        <option value="">Все статусы</option>
                        <option value="pending">Ожидают</option>
                        <option value="approved">Выполнены</option>
                        <option value="rejected">Отклонены</option>
                    </select>
                    <button
                        onClick={loadWithdrawals}
                        disabled={loading}
                        className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 disabled:opacity-50"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} /> Обновить
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg">{error}</div>
            )}

            {loading && withdrawals.length === 0 ? (
                <div className="flex justify-center py-12">
                    <RefreshCw className="animate-spin text-slate-400" size={32} />
                </div>
            ) : withdrawals.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center text-slate-500">
                    Заявок нет.
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">ID</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Пользователь</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Сумма</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Тип</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Реквизиты</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Статус</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Дата</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Действия</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-200">
                                {withdrawals.map((w) => (
                                    <tr key={w.id} className="hover:bg-slate-50">
                                        <td className="px-4 py-3 text-sm text-slate-600">{w.id}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className="font-mono">@{w.owner_username || '—'}</span>
                                            <span className="text-slate-400 text-xs block">tg_id: {w.owner_tg_id ?? '—'}</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-semibold">{parseFloat(w.amount || 0).toFixed(2)} ₽</td>
                                        <td className="px-4 py-3 text-sm">{w.withdrawal_type === 'stars' ? '⭐ Звёзды' : '💵 USDT'}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" title={w.payment_details}>{w.payment_details || '—'}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`px-2 py-0.5 rounded text-xs ${
                                                w.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                                                w.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-rose-100 text-rose-800'
                                            }`}>
                                                {w.status === 'pending' ? 'Ожидает' : w.status === 'approved' ? 'Выполнено' : 'Отклонено'}
                                            </span>
                                            {w.admin_comment && <p className="text-xs text-indigo-600 mt-1">💬 {w.admin_comment}</p>}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{w.created_at ? new Date(w.created_at).toLocaleString('ru-RU') : '—'}</td>
                                        <td className="px-4 py-3">
                                            {w.status === 'pending' && (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleApprove(w.id)}
                                                        disabled={processingId !== null}
                                                        className="flex items-center gap-1 px-2 py-1.5 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200 disabled:opacity-50"
                                                    >
                                                        <Check size={14} /> Выполнено
                                                    </button>
                                                    <button
                                                        onClick={() => handleReject(w.id)}
                                                        disabled={processingId !== null}
                                                        className="flex items-center gap-1 px-2 py-1.5 bg-rose-100 text-rose-800 rounded text-sm hover:bg-rose-200 disabled:opacity-50"
                                                    >
                                                        <X size={14} /> Отклонить
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WithdrawalsPage;
